'use client';

import { ProductDetailsPageProps } from '~/features/product-details/types';
import { TextVariants } from '~/theme/text-styles';
import { P80FavoritePageProps } from '~/features/favorites/favorites.definitions';
import { StoreResponse } from '../salesforce';
import {
    CategoryItem,
    TCategoryPickerItem,
} from '~/features/productList/components/CategoryPickerItem/CategoryPickerItem';
import { ComponentType } from 'react';
import { MinimalStoreResponse } from '~/services/stores/store-api';
import { MapMarker } from '~/features/find-store/components/StoreMap/models';
import { SalesforceProduct } from '~/lib/salesforce/types';
import { PLPResult } from '~/features/productList/PLPModule.definitions';
import { InstantSearchTopQueries } from '~/services/instantsearch/types';
import { TopReview } from '~/services/yotpo';

export interface ModuleTheme {
    backgroundColor?: string;
    textColor?: string;
}

export interface ModuleSpacing {
    spacingTop?: ModuleSpace;
    spacingBottom?: ModuleSpace;
}

export type ModuleSpace = 'none' | 'xs' | 'small' | 'default';

export type ModuleMediaLayout = 'full' | 'split';

export type ModuleHeight = 'narrow' | 'normal' | 'tall';

export interface ModuleColorTheme {
    id: string;
    parentId?: string;
    type?: string;
    name: string;
    url: string;
    updateDate?: string;
    sortOrder?: number;
    hasChildren?: boolean;
    mainColor: string;
    secondaryColor: string;
    secondaryColorOpacity: string;
    highlightColor: string;
    foregroundColor: string;
    gradientHighlight: boolean;
    textColor: string;
}

export type SSR_Data = {
    timestamp?: number;
    product?: SalesforceProduct | null;
    productTopReviews?: TopReview[] | null;
    brands?: BrandLinks | null;
    plpResult?: PLPResult | null;
    topSearches?: InstantSearchTopQueries | null;
    /**
     * Stores and Store Details pages will use StoreResponse.
     * PLPs will use MinimalStoreResponse.
     */
    storeList?: MinimalStoreResponse | StoreResponse | null;
};

/**
 * The brands overview payload: brand name to brand page URL. Unrelated to `Brands`, which is
 * the CMS module item used by `M22BrandsModule`.
 */
export type BrandLinks = {
    [brandName: string]: string;
};

/** A product details page cannot render without a product and an array of reviews even when empty. */
export type PDP_SSR_Data = Omit<SSR_Data, 'product' | 'productTopReviews'> & {
    product: SalesforceProduct;
    productTopReviews: TopReview[];
};

/** The store pages always receive the list, even when it comes back empty. */
export type Store_SSR_Data = Omit<SSR_Data, 'storeList'> & {
    storeList: StoreResponse | null;
};

export interface M70MediaItem {
    image?: ImageMedia;
    imageAltText?: string;
    video?: VideoMedia;
}

export interface MediaAndTextItem {
    alias?: 'contentArticle';
    image?: ImageMedia;
    imageAltText?: string;
    video?: VideoMedia;
    headline?: string;
    description?: RichText;
    verticalAlignment?: VerticalAlignment;
    callToAction?: Link;
}

export type Category = ModuleTheme & {
    title?: string;
    alias?: 'category';
    image?: ImageMedia;
    imageAltText?: string;
    callToAction?: Link;
    imageShadow?: boolean;
    headline?: Headline;
    subHeadline?: Headline;
    subSubHeadline?: Headline;
    verticalAlignment?: VerticalAlignment;
    horizontalAlignment?: HorizontalAlignment;
    type?: 'category';
};

export type Brands = ModuleTheme & {
    alias?: 'category';
    image?: ImageMedia;
    imageAltText?: string;
    callToAction?: Link;
};

export type Module = (ModuleTheme & ModuleSpacing) & {
    id: string;
    type: string;
    priority?: boolean;
    aboveTheFold?: boolean;
    isLastItem?: boolean;
};

export type Modules =
    | M10HeroModule
    | M11HeroModule
    | M15HeroPromotionModule
    | M20CategoriesModule
    | M22BrandsModule
    | M21ReferencedCategoriesModule
    | M30ProductCarouselModule
    | M31RaptorProductCarouselModule
    | M60UspModule
    | M70MediaModule
    | M75MediaAndTextModule
    | M80MediaAndTextBundleModule
    | M90QuoteModule
    | M100RichTextModule
    | M120AccordionListModule
    | M115ShopTheLookModule
    | M140AnchorModule
    | (M10HeroModule &
          M11HeroModule &
          M15HeroPromotionModule &
          M20CategoriesModule &
          M22BrandsModule &
          M21ReferencedCategoriesModule &
          M31RaptorProductCarouselModule &
          M60UspModule &
          M70MediaModule &
          M75MediaAndTextModule &
          M80MediaAndTextBundleModule &
          M90QuoteModule &
          M100RichTextModule &
          M120AccordionListModule)
    | M12Hero5050Module
    | M13Countdown5050Module
    | M40ContentCardRow;

export type StoreModules =
    | StoreTextSpotModule
    | StoreImageSpotModule
    | StoreOpeningHoursSpotModule
    | StoreDetailSpotModule;

export type ButtonColorProps =
    | 'link'
    | 'primary'
    | 'primary-inverse'
    | 'black-friday'
    | 'christmas'
    | 'secondary'
    | 'campaign'
    | 'brand'
    | 'plain'
    | 'icon'
    | 'filter'
    | 'plp'
    | 'inverse-plp'
    | 'tag'
    | 'pdp'
    | 'pdp-secondary'
    | 'alternative'
    | 'new-link'
    | 'new-link-black-friday'
    | 'new-link-christmas'
    | 'new-secondary';

export type M10HeroModule = Module &
    VideoSchema & {
        type: 'm10HeroModule';
        mediaLayout: ModuleMediaLayout;
        image?: ImageMedia;
        imageAltText?: string;
        video?: VideoMedia;
        mediaHorizontalAlignment: HorizontalAlignment;
        height?: ModuleHeight;
        horizontalAlignment?: HorizontalAlignment;
        verticalAlignment?: VerticalAlignment;
        headline?: Headline;
        subHeadline?: Headline;
        subSubHeadline?: Headline;
        description?: RichText;
        callToAction?: Link;
        color?: ButtonColorProps;
        alternativeBackgroundColor?: string;
        alternativeTextColor?: string;
    };

export type VideoSchema = {
    videoFile?: {
        id: number;
        name: string;
        src: string;
        extension: string;
        type: string;
    };
    videoThumbnail?: {
        id?: number;
        name?: string;
        crops?: any[];
        src: string;
        extension?: string;
        height?: number;
        width?: number;
        ratio?: number;
        type?: string;
    };
    videoPublishDate?: string;
    videoTitle?: string;
    videoDescription?: string;
    videoDuration?: number;
    videoLoop?: boolean;
    videoMute?: boolean;
    videoAutoPlay?: boolean;
    videoTheaterMode?: boolean;
};

export type M11HeroModule = Module &
    VideoSchema & {
        id?: string;
        type: 'm11HeroModule';
        trumpet?: string;
        title?: string;
        titleSize?: string;
        paragraphText?: string;
        disclaimer?: string;
        reverseContent?: boolean;
        link?: {
            text?: string;
            title?: string;
            url?: string;
            id?: string;
            target?: string;
        };
        colorTheme: ModuleColorTheme;
        image: {
            id?: number;
            name?: string;
            crops?: any[];
            src: string;
            extension?: string;
            height?: number;
            width?: number;
            ratio?: number;
            type?: string;
        };
        video?: VideoMedia;
        imageAltText?: string;
        watermark: string;
        imageOnBackground: boolean;
    };

export type M15HeroPromotionModule = (Module & VerticalAlignment) & {
    type: 'm15HeroPromotionModule';
};

export type M20CategoriesModule = Module & {
    type: 'm20CategoriesModule';
    items?: Category[];
    headline?: Headline;
    shownAs?: 'Standard' | 'Carousel' | 'Tiles';
};

export type M22BrandsModule = Module & {
    type: 'm22BrandsModule';
    items?: Brands[];
    headline?: string;
    callToAction?: Link;
};

export type M21ReferencedCategoriesModule = Module & {
    type: 'm21ReferencedCategoriesModule';
    items?: PageReference[];
    headline?: Headline;
    subHeadline?: Headline;
    callToAction?: Link;
};

export enum UmbracoAlgoliaCategories {
    'Category Id' = 'cust_categories.id',
    'Brand Id' = 'brand',
    'Series Id' = 'cust_series',
    'Context Rule Name' = '',
    'Custom' = 'custom',
}

export type M30ProductCarouselModule = Module & {
    type: 'm30ProductCarouselModule';
    headline?: Headline;
    subHeadline?: Headline;
    callToAction?: Link;
    algoliaFilter: string;
    algoliaFilterType?: keyof typeof UmbracoAlgoliaCategories;
};

export type RaptorDropdownTypes =
    | 'GetOverAllTopSelling'
    | 'GetCookieRecommendations'
    | 'RelatedOrSimilarProductsOthersBought'
    | 'GetRelatedItems'
    | 'RelatedAndSimilarProductsOthersBought'
    | 'GetRelatedItemsForBasket'
    | 'GetRelatedItemsForMiniBasket'
    | 'GetTopSellingInCategory'
    | 'GetTopPurchasedInBrands'
    | 'GetCookieHistory'
    | 'GetTrendingBrands'
    | 'GetSimilarItems'
    | 'GetTrendingItems'
    | 'GetPopularItemsInCategoryWeb'
    | 'GetPopularItemsInBrandWeb'
    | 'GetUserItemRecommendationsWeb'
    | 'GetMerchandisingItemsWeb'
    | 'GetPopularItemsWeb';

export type M31RaptorProductCarouselModule = Module & {
    type: 'm31RaptorCarouselModule';
    headline?: Headline;
    subHeadline?: Headline;
    callToAction?: Link;
    raptorAlgorithm: RaptorDropdownTypes;
    raptorParameter?: string;
    merchandisingCampaignId?: string;
};

export type M60UspModule = Module & { type: 'm60UspModule'; items: UspItem[] };

export type M70MediaModule = (Module & M70MediaItem) & { type: 'm70MediaModule'; caption?: string };

export type M75MediaAndTextModule = Module &
    VideoSchema & {
        type: 'm75MediaAndTextModule';
        mediaSize: 'default' | 'small';
        image?: ImageMedia;
        imageAltText?: string;
        video?: VideoMedia;
        headline?: Headline;
        subHeadline?: Headline;
        subSubHeadline?: Headline;
        text?: RichText;
        callToAction?: Link;
        color: 'link' | 'primary' | 'secondary' | 'brand' | 'campaign';
        mediaHorizontalAlignment: HorizontalAlignment;
        verticalAlignment?: VerticalAlignment;
        horizontalAlignment?: HorizontalAlignment;
        mediaBackgroundColor: string;
        alternativeBackgroundColor?: string;
        alternativeTextColor?: string;
    };

export type M80MediaAndTextBundleModule = Module & {
    type: 'm80MediaAndTextBundleModule';
    headline?: Headline;
    callToAction?: Link;
    mediaAndTextItems: M80MediaAndTextBundleItem[];
};

export type M80MediaAndTextBundleItem = MediaAndTextItem | PageReference;

export type M90QuoteModule = Module & { type: 'm90QuoteModule'; quote?: string; author?: string };

export type M140AnchorModule = { id: string; type: 'm140AnchorModule'; identifier: string };

export type M100RichTextModule = Module & {
    type: 'm100RichTextModule';
    headline?: Headline;
    subHeadline?: Headline;
    subSubHeadline?: Headline;
    text?: RichText;
};
//Store details
export type StoreTextSpotModule = Module & {
    type: 'storeTextSpot';
    text?: RichText;
    border?: string;
};

export type StoreDetailSpotModule = Module & {
    type: 'storeDetailModule';
    text?: RichText;
    border?: string;
    displayStoreStatus?: boolean;
    storeList?: MapMarker;
};

export type StoreImageSpotModule = Module & {
    type: 'storeImageSpot';
    image?: ImageMedia;
    imageAltText?: string;
    border?: string;
};

export type StoreOpeningHoursSpotModule = Module & {
    type: 'storeOpeningHoursSpot';
    border?: string;
    storeList?: MapMarker;
};

export type M120AccordionListModule = Module & {
    type: 'm120AccordionListModule';
    headline?: Headline;
    accordionItems: AccordionItem[];
};

interface ColorTheme {
    mainColor: string;
    secondaryColor: string;
    secondaryColorOpacity: string;
    highlightColor: string;
    foregroundColor: string;
    gradientHighlight: boolean;
    textColor: string;
}

export type Hero5050Props = Module & {
    trumpet?: string;
    title?: string;
    paragraphText?: string;
    link?: Link;
    watermark?: 'Logo' | 'Percentage' | '' | null;
    imageOnBackground?: boolean;
    reverseContent?: boolean;
    colorTheme: ColorTheme;
    disclaimer?: string;
    imageAltText?: string;
    image?: ImageMedia;
    linkType?: 'Primary' | 'Secondary' | 'Text';
    titleSize?: 'Extra Large' | 'Large' | 'Medium';
};

export type M12Hero5050Module = Hero5050Props &
    VideoSchema & {
        type: 'm12Hero5050Module';
        video?: VideoMedia;
    };

export type M13Countdown5050Module = Hero5050Props & {
    type: 'm13Countdown5050Module';
    startImageimageAltText?: string;
    startImage?: ImageMedia;
    endImageimageAltText?: string;
    endImage?: ImageMedia;
    countdownEndDate?: Date | null;
};

export type M115ShopTheLookHotspot = {
    title: string;
    description?: string;
    positionX: number;
    positionY: number;
    link?: Link;
};

export type M115ShopTheLookModule = Module & {
    type: 'm115ShopTheLookModule';
    title: string;
    paragraphText?: RichText;
    image: ImageMedia;
    imageAltText: string;
    hotspots: M115ShopTheLookHotspot[];
    colorTheme: ColorTheme;
    reverseContent: boolean;
    trumpet?: string;
};

export interface ContentCard {
    alias: 'contentCardText' | 'contentCardImage';
    cardText?: string;
    cardTitle?: string;
    cardTrumpet?: string;
    imageAltText?: string;
    image?: ImageMedia;
    watermark?: 'Logo' | 'Percentage' | '' | null;
    textOnBackground?: boolean;
    imageOnBackground?: boolean;
    trumpet?: string;
    header?: string;
    paragraph?: string;
    link?: Link;
    linkType?: 'Primary' | 'Secondary' | 'Text';
}

export type M40ContentCardRow = Module & {
    type: 'm40ContentCardRow';
    title?: string;
    colorTheme: ColorTheme;
    contentCards: ContentCard[];
};

/* eslint-disable */
export type PageTypes = {
    p60ModulePage: ComponentType | any;
    p100CheckoutPage: ComponentType | any;
    p90BasketPage: ComponentType | any;
    p90BasketPage1: ComponentType | any;
    p150ErrorPage: ComponentType | any;
    p160ArticlePage: ComponentType | any;
    articleListPage: ComponentType | any;
    p125MyProfilePage: ComponentType | any;
    receiptPage: ComponentType | any;
    p40ProductListPage: ComponentType | any;
    p80FavoritePage: ComponentType | any;
    p70FindStores: ComponentType | any;
    p71StoreDetailsPage: ComponentType | any;
    brandsListPage: ComponentType | any;
    p50ProductDetailsPage: ComponentType | any;
    p10SearchPage: ComponentType | any;
    giftCardBalancePage: ComponentType | any;
    p20FrontPage: ComponentType | any;
    p170AviouPage: ComponentType | any;
};
/* eslint-enable */

export type Pages =
    | P60ModulePage
    | P100CheckoutPage
    | P90BasketPage
    | P160ArticlePage
    | P165ArticleListPage
    | P125MyProfilePage
    | ReceiptPage
    | P40ProductListPage
    | P80FavoritePageProps
    | P70FindStoresPage
    | P71StoreDetailsPage
    | BrandsListPage
    | ProductDetailsPageProps
    | P10SearchPage
    | GiftCardBalancePage
    | P170AviouPage
    | P20FrontPage;

export type ModulePage = Page;

export type OpenGraph = {
    title?: string;
    description?: string;
    image?: ImageMedia | null;
    type?: string;
} & {
    [key: string]: string;
};

export type Page = (Globalizable & Localizable) & {
    meta?: {
        url?: string;
        title?: string;
        description?: string;
        hideFromRobots?: boolean;
        canonical?: Link;
        openGraph?: OpenGraph;
        schemaMarkup?: string;
        keywords?: string;
    };
    statusCode?: number;
    buttonColor?: ButtonColorProps;
    buttonLink?: {
        id: string;
        text: string;
        title: string;
        url: string;
    };
    pointsHeader?: string;
    pageElements?: PageElement[];
    bottomPageElements?: PageElement[];
    productListPageSize?: number;
    breadcrumb?: Breadcrumb;
    announcementBar?: AnnouncementBar;
    productCategoryLinks?: Record<string, any>;
    filterBottomContent?: boolean;
    id: string;
    type: string;
    isMobile?: boolean;
    categories?: CategoryItem[];
    enableAIProductGuideBanner?: boolean;
    /**
     * @data can be extended to have other data types if needed
     * It is added on page level to make it more easy to extend
     * and is a place for different page type to define their additional
     * data types.
     */
    data?: SSR_Data;
};

export type PersonalizedPage = (Globalizable & Localizable) & {
    pageElements: PageElement[];
    id: string;
    type: string;
};

export type P60ModulePage = ModulePage & { type: 'p60ModulePage' };

export type P60ModulePage1 = ModulePage & { type: 'p60ModulePage1' };

export type P125MyProfilePage = ModulePage & { type: 'p125MyProfilePage' };
export type BrandsListPageProps = ModulePage & { type: 'brandsListPage' };

export type P160ArticlePage = ModulePage & {
    type: 'p160ArticlePage';
    article: Article;
    relatedArticles?: Article[];
    heroHeader?: string;
    heroImage: ImageMedia;
    heroImageAltText?: string;
};
export type Article = {
    createdBy: string;
    productListHeader?: string;
    productIds?: string[];
    url: string;
    pageTitle: string;
    pageDescription: string;
    pageImage: Media;
    heroImage?: Media;
    pageImageAltText?: string;
    parentName: string;
    tags: string[];
    creationDate: string;
    heroTheme?: 'Light' | 'Dark';
};

export type P165ArticleListPage = ModulePage & {
    type: 'articleListPage';
    articles: Article[];
    categories?: Link[];
    heroHeader?: string;
    heroTheme?: 'Light' | 'Dark';
    heroImage: ImageMedia;
    heroImageAltText?: string;
    middlePageElements: PageElement[];
    callToAction?: Link;
    headline?: Headline;
    subHeadline?: Headline;
};

export type P170AviouPage = ModulePage & {
    type: 'p170AviouPage';
    aviouId: string;
    aviouUrl: string;
};

export type P100CheckoutPage = ModulePage & { type: 'p100CheckoutPage' };

export type P90BasketPage = ModulePage & { type: 'p90BasketPage' };

export type P20FrontPage = ModulePage & { type: 'p20FrontPage' };

export type P10SearchPage = ModulePage & { type: 'p10SearchPage' };

export type ReceiptPage = ModulePage & { type: 'receiptPage' };
export type BrandsListPage = ModulePage & { type: 'brandsListPage' };

export type P70FindStoresPage = ModulePage & {
    type: 'p70FindStores';
    data: Store_SSR_Data;
};

export type P71StoreDetailsPage = ModulePage & {
    type: 'p71StoreDetailsPage';
    storeId: string;
    image: ImageMedia;
    imageAlt: string;
    description: string;
    data: Store_SSR_Data;
    pageElements?: PageElement[];
};

export type GiftCardBalancePage = ModulePage & {
    type: 'giftCardBalancePage';
    page: { headline?: Headline; descriptionText?: string; pageElements?: PageElement[] };
};

export type P40ProductListPage = ModulePage & {
    dataConfiguration: string;
    dataType?: keyof typeof UmbracoAlgoliaCategories;
    parentDataType?: keyof typeof UmbracoAlgoliaCategories;
    parentDataConfiguration?: string;
    heroImage: ImageMedia;
    heroImageAltText: string;
    type: 'p40ProductListPage';
    categories?: TCategoryPickerItem[];
    data?: SSR_Data;
};

export type HeaderTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'p';

export type HeaderStyle = TextVariants;

export type HorizontalAlignment = 'left' | 'right';

export type VerticalAlignment = 'top' | 'center' | 'bottom';

export interface Product {
    name?: string;
    url?: string;
    image?: ImageMedia;
}

export interface ProductDetails {
    images?: ImageMedia[];
    variations?: Variation[];
    categories?: ProductCategory[];
    splashes?: Splash[];
}

export type ProductCategory = object;

export interface AccordionItem {
    headline: string;
    description: RichText;
}

export type RichText = string;

export interface Headline {
    text?: string;
    tag: HeaderTag;
    style?: HeaderStyle;
    textColor?: string;
}

export interface Media {
    type?: string;
    id: string;

    /** Source of media. */
    src: string;
    width: number;
    height: number;
    caption?: string;
    name?: string;
}

export type FocalPoint = { left: number; top: number };

export type ImageMedia = Media & { alt?: string; focalPoint?: FocalPoint };

export interface VideoMedia {
    src: string;

    /** Should the video loop */
    loop: boolean;

    mute: boolean;

    theaterMode: boolean;

    /** Should the video start on load */
    autoPlay: boolean;
}

export type Basket = (Globalizable & Localizable) & {
    id: string;
    quantitySum: number;
    totals: Totals;
    lineItems: LineItem[];
};

export interface ValidatedBasketViewModel {
    success?: boolean;
    validationMessages?: string[];
    culture?: string;
    market?: string;

    /** @format double */
    quantitySum?: number;
    totals?: Totals;
    lineItems?: LineItem[];
    type?: string;
    id?: string;
}

export interface UspItem {
    id: string;
    image: ImageMedia;
    imageAltText?: string;
    headline: string;
    text?: string;
    link?: Link;
}

export interface Totals {
    subTotal: number;
    shippingTotal: number;
    taxTotal: number;
    handlingTotal: number;
    total: number;
}

export interface LineItem {
    variation: Variation;

    /** @format double */
    quantity: number;
    unitPrice: Money;
    discountedUnitPrice?: Money;
    linePrice: Money;
    discountedLinePrice?: Money;
    id?: string;
}

export interface StockStatus {
    quantityAvailable: number;
}

export type Variation = (Globalizable & Localizable) & {
    sku: string;
    name: string;
    url?: string;
    image?: ImageMedia;
    type: string;
};

export type VariationDetails = Variation & {
    images?: ImageMedia[];
    price: Money;
    discountedPrice?: Money;
    stockStatus: StockStatus;
};

export interface Splash {
    text?: string;
}

export interface Money {
    /** @format double */
    amount: number;
}

export interface Frame {
    header?: Header;
    footer?: Footer;
    navigation?: Navigation;
    search?: Search;
    market: Market;
    marketSelector?: MarketSelector;
    miniBasketSettings?: MiniBasketSettings;
    productSettings?: ProductSettings;
    settings?: SiteSettings;
    id?: string;
    culture?: string;
    type?: string;
}

export interface MiniBasketSettings {
    headline?: string;
    merchandisingCampaignId?: string;
    raptorAlgorithm: RaptorDropdownTypes;
    raptorParameter?: string;
}

export interface Member {
    memberToken: string;
    memberSystemId: string;
    marketingPreferences: MarketingPreferences;
}

export interface MarketingPreferences {
    smsAllowed: boolean;
    emailAllowed: boolean;
    soMeAllowed: boolean;
}

export interface Header {
    id?: string;
    textColor?: string;
    backGroundColor?: string;
    campaignBannerText?: string;
    campaignBannerLink?: Link;
    topBannerTextColor?: string;
    topBannerBackGroundColor?: string;
    topBanner?: string;
    inStoreBanner?: string;
    inStoreBackgroundColor?: string;
    inStoreTextColor?: string;
    uspTextColor?: string;
    uspBackgroundColor?: string;
}

export interface Footer {
    id?: string;
    contactText?: RichText;
    logoImage?: ImageMedia;
    newsletterText?: RichText;
    newsletterLink?: Link;
    menu?: FooterNavigationNode[];
    paymentIcons?: FooterIcon[];
    additionalIcons?: FooterIcon[];
    trustpilotLink: Link;
    rate: RichText;
}

export interface FooterNavigationNode {
    header: string;
    children?: SubFooterNavigationNode[];
    id?: string;
}

export interface SubFooterNavigationNode {
    link: Link;
    id?: string;
}

export interface FooterIcon {
    image: ImageMedia;
    title?: string;
    link?: Link;
}

export interface Navigation {
    mainMenu?: NavigationNode[];
    metaMenu?: MetaNavigationNode;
    id?: string;
}

export interface Search {
    id?: string;
    SearchShortcuts: Category[];
    SearchPageSize?: number;
}

export interface ReceiptData {
    header: string;
    standardDeliveryHeader: string;
    supportHeadline: string;
    supportText: string;
    SupportPhoneButton: Link;
}

export interface BasicUspItem {
    headline: string;
    text: string;
}

export interface ProductUspItem extends BasicUspItem {
    headline: string;
    text: string;
    tags: string[];
    icon?: ImageMedia;
    tooltip?: string;
}

export interface FaqItem {
    headline: string;
    description: string;
}

export interface Market {
    currency?: Currency;
    pricesIncludeTax?: boolean;
    id?: string;
    orderRequirements?: string;
    orderRequirementTitle?: string;
    orderRequirementsLink?: string;
    membershipTermsConsentLabel?: string;
    membershipTermsContent?: string;
    membershipTermsLink?: string;
    personalDataPolicyLabel?: string;
    personalDataPolicyContent?: string;
    personalDataPolicyLink?: string;
    checkoutUsps?: BasicUspItem[];
    uniqueSellingPoints?: ProductUspItem[];
    checkoutFaq?: FaqItem[];
    receipt?: ReceiptData;
    deliveryBannerInfo?: string;
    freightPolicyText?: string;
    freightPolicyTitle?: string;
    minimumBasketTotalForFreeShipping?: number;
    reservationSuccessText?: string;
}

export interface MarketSelector {
    markets?: MarketSelectorMarket[];
}

export interface Localizable {
    culture: string;
}

export interface Globalizable {
    market: string;
}

export interface PaymentOption {
    id: string;
    label: string;
    isEnabled?: boolean;
}

export interface SiteSettings {
    googleTagManagerId?: string;
    googleTagManagerEnvironment?: string;
    raptorCustomerId?: string;
    pageReferences: PageReferences;
    paymentOptions?: PaymentOption[];
    klarnaMinimumAmountThreshold?: number;
    countdownColorTheme?: ModuleColorTheme;
    enableAIChatbot?: boolean;
}

export interface ProductSettings {
    productLabels: ProductLabelSetting[];
    showFreightPrice: boolean;
    showSortByRatings?: boolean;
    pageSize: number;
    shouldAllowPersonalization?: boolean;
    shouldShowBurdBanner?: boolean;
    sortingMethods?: {
        label: string;
        value: string;
    }[];
    gpsrDefault?: string;
    gpsrExclusive?: string;
    klarnaBadgeEnabled?: boolean;
    freightByCarrierUsp?: ProductUspItem[];
    aiGuideBannerPrompt?: string;
}

export interface ProductLabelSetting {
    textColor: string;
    backgroundColor: string;
    displayText: string;
    conditions: ProductLabelCondition[];
}

export interface ProductLabelCondition {
    productField: {
        alias: string;
        type: 'bolean' | 'string' | 'number';
    };
    operator: 'is' | 'is not' | 'contains';
    logicalOperator?: 'AND' | 'OR';
    value: unknown;
}

export interface PageReferences {
    basketPage?: Link;
    checkoutPage?: Link;
    receiptPage?: Link;
    productPage?: Link;
    searchPage?: Link;
    termsAndConditionsPage?: Link;
    gdprPage?: Link;
    storesPage?: Link;
    notFoundErrorPage?: Link;
    serverErrorPage?: Link;
    loginPage?: Link;
    myProfilePage?: Link;
    createLoginPage?: Link;
    favoritesPage?: Link;
    brandsOverviewPage?: Link;
    campaignPage?: Link;
    preferenceCenterPage: Link;
    mixAndMatchPage?: Link;
    bundlePage?: Link;
    bastardPage?: Link;
}

export interface Redirect {
    type: 'redirect';
    destination: string;
    permanent: boolean;
}

export interface NavigationNode {
    link: Link;
    image?: ImageMedia;
    mobileImage?: ImageMedia;
    alternateLinkHeader?: string;
    children?: NavigationNode[];
    promotedChildren?: PromotedNavigationNode[];
    id?: string;
}

export interface MetaNavigationNode {
    bottomLink: Link;
    links?: Link[];
    DesktopLinks?: NavigationNode[];
    UspLinks?: UspItem[];
    mobileLinks?: NavigationNode[];
    id?: string;
}

export interface PromotedNavigationNode {
    image: ImageMedia;
    link: Link;
    text?: string;
}

export interface Currency {
    symbol?: string;
    currency?: string;
    id?: string;
}

export interface MarketSelectorMarket {
    label?: string;
    languages?: MarketSelectorLanguage[];
    id?: string;
}

export interface Link {
    text?: string;
    title?: string;
    url?: string;
    target?: string;
    id?: string;
}

export interface MarketSelectorLanguage {
    text?: string;
    title?: string;
    url?: string;
    target?: string;
    id?: string;
    culture?: string;
}

export interface MarketBanner {
    text?: string;
    buttonText?: string;
    dismissButtonText?: string;
    selectOtherMarketText?: string;
    selectMarketLabel?: string;
    marketLink?: Link;
}

export interface PageElement {
    id?: string;
    type?: string;
}

export interface PageReference {
    parentName?: string;
    alias?: 'pageReference';
    pageTitle?: string;
    pageDescription?: string;
    pageImage?: ImageMedia;
    callToAction?: Link;
}

export interface Breadcrumb {
    items?: Link[];
}

export interface AnnouncementBar {
    textAlignment?: string;
    textColor?: string;
    backgroundColor?: string;
}

export interface BasketListParams {
    culture: string;
    market: string;
}

export interface BasketLineCreateParams {
    culture: string;
    market: string;
    sku: string;

    /** @format double */
    quantity: number;
}

export interface BasketLineUpdateParams {
    culture: string;
    market: string;

    /** @format double */
    quantity: number;
    sku: string;
}

export interface BasketLineDeleteParams {
    culture: string;
    market: string;
    sku: string;
}

export interface ContentFrameListParams {
    url: string;
}

export interface ContentPersonalizedFrameListParams {
    url: string;
}

export interface ContentMarketBannerListParams {
    pageId: string;
    culture: string;
    market: string;
    detectedMarket?: string;
}

export interface ContentModuleListParams {
    pageId: string;
    culture: string;
    market: string;
    moduleId: string;
}

export interface ContentPageListParams {
    url: string;
}

export interface ContentPersonalizedPageListParams {
    url: string;
}

export interface ContentTranslationsListParams {
    culture: string;
}

export interface MarketingSubscriptionListParams {
    email: string;
}

export interface MarketingSubscriptionCreateParams {
    email: string;
}

export interface MarketingSubscriptionDeleteParams {
    email: string;
}

export interface SearchAutoCompleteListParams {
    culture: string;
    market: string;
    query?: string;
    id?: string;
    pageSize?: number;
}

export interface SearchResultBySearchIdListParams {
    culture: string;
    market: string;
    query?: string;
    searchId?: string;
    pageSize?: number;
    page?: number;
}

export interface SearchResultListParams {
    culture: string;
    market: string;
    query?: string;
    category?: string;
    pageSize?: number;
    page?: number;
}
